// Component defaults that carry the Safrati standard (docs/ui-standard.md):
// pill buttons (small 40 / medium 48 / large 52, 44 on touch), fields 14 with
// the `field` fill and a brand focus ring, cards 20 with a soft shadow, and a
// visible 2px focus ring on everything clickable.
import { focusFirstField } from 'src/components/shared/keyboard';

// A touch screen, or a window as narrow as a phone.
const touch = '@media (pointer: coarse), (max-width: 599.95px)';

export default function components(theme) {
  const dark = theme.palette.mode === 'dark';
  const t = theme.palette.safrati || {};
  const ring = dark ? '#ffffff' : theme.palette.primary.main;
  const focusRing = { outline: `2px solid ${ring}`, outlineOffset: '2px' };
  const insetRing = { outline: `2px solid ${ring}`, outlineOffset: '-2px' };
  return {
    MuiCssBaseline: {
      styleOverrides: {
        '*': {
          boxSizing: 'border-box',
        },
        html: {
          height: '100%',
          width: '100%',
        },
        a: {
          textDecoration: 'none',
          color: theme.palette.primary.main,
        },
        'a:focus-visible': focusRing,
        body: {
          height: '100%',
          margin: 0,
          padding: 0,
          WebkitFontSmoothing: 'antialiased',
        },
        '@media (prefers-reduced-motion: reduce)': {
          '*, *::before, *::after': {
            animationDuration: '0.01ms !important',
            animationIterationCount: '1 !important',
            transitionDuration: '0.01ms !important',
            scrollBehavior: 'auto !important',
          },
        },
        // Toasts follow the theme (react-toastify's own colours are loud and
        // ignore dark mode).
        ':root': {
          '--toastify-color-light': theme.palette.background.paper,
          '--toastify-text-color-light': theme.palette.text.primary,
          '--toastify-color-success': theme.palette.success.main,
          '--toastify-color-error': theme.palette.error.main,
          '--toastify-color-warning': theme.palette.warning.main,
          '--toastify-color-info': theme.palette.primary.main,
          '--toastify-font-family': theme.typography.fontFamily,
          '--toastify-toast-min-height': '56px',
        },
        '.Toastify__toast': {
          borderRadius: 14,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: theme.shadows[9],
          fontSize: '0.9375rem',
          lineHeight: 1.45,
        },
        '.Toastify__close-button': { color: theme.palette.text.secondary, opacity: 0.8 },
        // Toasts sit just under the sticky top bar, centred, so they never
        // cover a page's actions (top right) or a save bar (bottom).
        '.Toastify__toast-container--top-center': {
          top: 76,
          '@media (max-width: 480px)': { top: 68, padding: '0 12px', left: 0, right: 0, width: '100%' },
        },
        '#root': {
          height: '100%',
        },
        "*[dir='rtl'] .buyNowImg": {
          transform: 'scaleX(-1)',
        },
        '.border-none': {
          border: '0px',
          td: {
            border: '0px',
          },
        },
        '.btn-xs': {
          minWidth: '30px !important',
          width: '30px',
          height: '30px',
          borderRadius: '6px !important',
          padding: '0px !important',
        },
        '.hover-text-primary:hover .text-hover': {
          color: theme.palette.primary.main,
        },
        '.hoverCard:hover': {
          scale: '1.01',
          transition: ' 0.1s ease-in',
        },
        '.signup-bg': {
          position: 'absolute',
          top: 0,
          right: 0,
          height: '100%',
        },
        '.MuiBox-root': {
          borderRadius: theme.shape.borderRadius,
        },
        '.MuiCardHeader-action': {
          alignSelf: 'center !important',
        },
        '.emoji-picker-react .emoji-scroll-wrapper': {
          overflowX: 'hidden',
        },
        '.scrollbar-container': {
          borderRight: '0 !important',
        },
        '.theme-timeline .MuiTimelineOppositeContent-root': {
          minWidth: '90px',
        },
        '.MuiAlert-root .MuiAlert-icon': {
          color: 'inherit!important',
        },
        '.MuiTimelineConnector-root': {
          width: '1px !important',
        },
        ' .simplebar-scrollbar:before': {
          background: `${theme.palette.grey[300]} !important`,
        },
        '@keyframes gradient': {
          '0%': {
            backgroundPosition: '0% 50%',
          },
          '50%': {
            backgroundPosition: ' 100% 50%',
          },
          '100% ': {
            backgroundPosition: ' 0% 50%',
          },
        },
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          '&.Mui-focusVisible': focusRing,
        },
      },
    },
    MuiButtonGroup: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          ':before': {
            backgroundColor: theme.palette.grey[100],
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        rounded: {
          borderRadius: 14,
        },
        outlined: {
          borderColor: theme.palette.divider,
        },
      },
    },
    MuiStepConnector: {
      styleOverrides: {
        line: {
          borderColor: theme.palette.divider,
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
        },
        sizeSmall: {
          width: 30,
          height: 30,
          minHeight: 30,
        },
      },
    },
    // The same look as ActionButton (src/components/shared/ActionButton.js),
    // so a plain MUI Button already matches the standard.
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 999,
          textTransform: 'none',
          fontWeight: 600,
          letterSpacing: 0,
          boxShadow: 'none',
          whiteSpace: 'nowrap',
          transition: `${theme.transitions.create(['background-color', 'border-color', 'color', 'box-shadow'], {
            duration: 140,
          })}, transform 60ms ease-out`,
          '&:active': { transform: 'scale(1.03)' },
          '@media (prefers-reduced-motion: reduce)': { '&:active': { transform: 'none' } },
          '@media (pointer: coarse)': { minHeight: 44 },
          '& .MuiButton-startIcon': { marginLeft: 0, marginRight: 8 },
          '& .MuiButton-endIcon': { marginLeft: 8, marginRight: 0 },
        },
        sizeSmall: { minHeight: 40, padding: '0 16px', fontSize: '0.875rem' },
        sizeMedium: { minHeight: 48, padding: '0 24px', fontSize: '0.9375rem' },
        sizeLarge: { minHeight: 52, padding: '0 28px', fontSize: '1.0625rem' },
        text: { '&.MuiButton-sizeMedium': { padding: '0 16px' }, '&.MuiButton-sizeSmall': { padding: '0 12px' } },
        containedPrimary: {
          boxShadow: dark ? 'none' : '0 4px 12px rgba(0, 74, 173, 0.22)',
          '&:hover': {
            backgroundColor: theme.palette.primary.dark,
            boxShadow: dark ? 'none' : '0 4px 12px rgba(0, 74, 173, 0.22)',
          },
        },
        outlined: {
          backgroundColor: theme.palette.background.paper,
          borderColor: t.control,
        },
        outlinedPrimary: {
          borderColor: t.control,
          '&:hover': {
            backgroundColor: theme.palette.primary.light,
            borderColor: theme.palette.primary.main,
          },
        },
        outlinedSecondary: {
          '&:hover': { backgroundColor: theme.palette.secondary.light },
        },
        outlinedError: {
          '&:hover': { backgroundColor: theme.palette.error.light },
        },
        outlinedSuccess: {
          '&:hover': { backgroundColor: theme.palette.success.light },
        },
        outlinedInfo: {
          '&:hover': { backgroundColor: theme.palette.info.light },
        },
        outlinedWarning: {
          '&:hover': { backgroundColor: theme.palette.warning.light },
        },
        textPrimary: {
          '&:hover': { backgroundColor: theme.palette.primary.light },
        },
        textSecondary: {
          '&:hover': { backgroundColor: theme.palette.secondary.light },
        },
        textError: {
          '&:hover': { backgroundColor: theme.palette.error.light },
        },
        disabled: {},
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&:hover': { backgroundColor: theme.palette.action.hover },
          // Every icon button is at least 44 x 44 to a finger: an invisible
          // hit area round small ones (copy, ⋮, close), and on a touch screen
          // or a phone-width window the button itself.
          '&::after': { content: '""', position: 'absolute', top: '50%', left: '50%', width: 44, height: 44, transform: 'translate(-50%, -50%)' },
          [touch]: { minWidth: 44, minHeight: 44 },
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          padding: '16px 24px',
        },
        title: {
          fontSize: '1.125rem',
          fontWeight: 600,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          width: '100%',
          padding: '15px',
          backgroundImage: 'none',
          borderRadius: 20,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: theme.shadows[1],
          '&.MuiPaper-outlined': { boxShadow: 'none' },
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '24px',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${theme.palette.divider}`,
        },
        head: {
          color: theme.palette.text.secondary,
          fontWeight: 600,
          fontSize: '0.8125rem',
          lineHeight: 1.3,
          whiteSpace: 'nowrap',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:last-child td': {
            borderBottom: 0,
          },
          '&.MuiTableRow-hover:hover': {
            backgroundColor: theme.palette.action.hover,
          },
          '&.Mui-selected, &.Mui-selected:hover': {
            backgroundColor: theme.palette.primary.light,
          },
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        toolbar: { flexWrap: 'wrap', rowGap: 4, paddingLeft: 8 },
        selectLabel: { fontSize: '0.8125rem' },
        displayedRows: { fontSize: '0.8125rem', fontVariantNumeric: 'tabular-nums' },
      },
    },
    MuiGridItem: {
      styleOverrides: {
        root: {
          paddingTop: '30px',
          paddingLeft: '30px !important',
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          backgroundColor: theme.palette.grey[200],
          borderRadius: '6px',
        },
      },
    },
    MuiTimelineConnector: {
      styleOverrides: {
        root: {
          backgroundColor: theme.palette.divider,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: theme.palette.divider,
        },
      },
    },
    // Chip text must reach 4.5:1. On light, the success / warning / accent
    // mains are too light for text (4.3, 3.7, 4.3 on white), so filled chips
    // in those colours use the soft tint with dark text (5.2, 5.2, 4.9) and
    // outlined ones dark text; dark mode's own colours already pass (6+).
    MuiChip: {
      styleOverrides: {
        // A chip that does something has a 44 px tall hit area.
        clickable: {
          position: 'relative',
          '&::after': { content: '""', position: 'absolute', left: 0, right: 0, top: '50%', height: 44, transform: 'translateY(-50%)' },
        },
        root: {
          fontWeight: 600,
          fontSize: '0.75rem',
          borderRadius: 999,
          ...(!dark &&
            Object.fromEntries(
              ['success', 'warning', 'secondary'].flatMap((tone) => {
                const key = tone.charAt(0).toUpperCase() + tone.slice(1);
                const c = theme.palette[tone];
                return [
                  [
                    `&.MuiChip-filled.MuiChip-color${key}`,
                    { backgroundColor: c.light, color: c.dark, '& .MuiChip-icon, & .MuiChip-deleteIcon': { color: c.dark } },
                  ],
                  [
                    `&.MuiChip-outlined.MuiChip-color${key}`,
                    { color: c.dark, borderColor: c.main, '& .MuiChip-icon, & .MuiChip-deleteIcon': { color: c.dark } },
                  ],
                ];
              }),
            )),
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 14,
          fontSize: '0.875rem',
          alignItems: 'flex-start',
        },
        filledSuccess: {
          color: 'white',
        },
        filledInfo: {
          color: 'white',
        },
        filledError: {
          color: 'white',
        },
        filledWarning: {
          color: 'white',
        },
        // Banner text and icon on their tint, all AA (4.5:1) or better:
        // light info 8.6, error 6.1, warning 5.2, success 5.2;
        // dark info 12.4, error 7.7, warning 8.1, success 7.4.
        standardSuccess: {
          backgroundColor: theme.palette.success.light,
          color: dark ? theme.palette.success.main : theme.palette.success.dark,
        },
        standardError: {
          backgroundColor: theme.palette.error.light,
          color: theme.palette.error.main,
        },
        standardWarning: {
          backgroundColor: theme.palette.warning.light,
          color: dark ? theme.palette.warning.main : theme.palette.warning.dark,
        },
        standardInfo: {
          backgroundColor: theme.palette.primary.light,
          color: dark ? theme.palette.text.primary : theme.palette.primary.dark,
        },
        outlinedSuccess: {
          borderColor: theme.palette.success.main,
          color: theme.palette.success.main,
        },
        outlinedWarning: {
          borderColor: theme.palette.warning.main,
          color: theme.palette.warning.main,
        },
        outlinedError: {
          borderColor: theme.palette.error.main,
          color: theme.palette.error.main,
        },
        outlinedInfo: {
          borderColor: theme.palette.info.main,
          color: theme.palette.info.main,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: { color: theme.palette.text.secondary },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: { marginLeft: 2, marginRight: 0, marginTop: 6, fontSize: '0.8125rem' },
      },
    },
    // Fields: 48 high, the `field` fill, a 14 radius and a 2px brand ring on
    // focus. size="small" stays 40 for toolbars and inline filters.
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 14,
          backgroundColor: t.field,
          transition: theme.transitions.create(['background-color', 'box-shadow'], { duration: 120 }),
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: t.line2,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: t.control,
          },
          '&.Mui-focused': {
            backgroundColor: theme.palette.background.paper,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
            borderWidth: 2,
          },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.error.main,
          },
          '&.Mui-disabled': {
            backgroundColor: theme.palette.action.disabledBackground,
          },
          '&:not(.MuiInputBase-multiline):not(.MuiInputBase-sizeSmall)': {
            minHeight: 48,
          },
          '&.MuiInputBase-sizeSmall': {
            minHeight: 40,
          },
        },
        input: {
          padding: '12px 14px',
        },
        inputSizeSmall: {
          padding: '8px 14px',
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: { display: 'flex', alignItems: 'center' },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: { borderRadius: 14, marginTop: 4 },
        list: { padding: 6 },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          minHeight: 40,
          fontSize: '0.9375rem',
          '&.Mui-focusVisible': { ...insetRing, backgroundColor: theme.palette.action.hover },
          [touch]: { minHeight: 44 },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          '&.Mui-focusVisible': insetRing,
          [touch]: { minHeight: 44 },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.9375rem',
          '&.Mui-focusVisible': insetRing,
          [touch]: { minHeight: 44 },
        },
      },
    },
    // A gap between a tick box, radio or switch and its words, so the focus
    // ring never covers the label's first letter.
    MuiFormControlLabel: {
      styleOverrides: {
        label: { paddingLeft: 4 },
      },
    },
    MuiSwitch: {
      // Every switch is read as a switch (on/off), not a tick box, unless it
      // passes inputProps of its own (those set role themselves; hala-32).
      defaultProps: { inputProps: { role: 'switch' } },
      styleOverrides: {
        root: {
          '& .Mui-focusVisible + .MuiSwitch-track': { outline: `2px solid ${ring}`, outlineOffset: '2px' },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          color: theme.palette.background.paper,
          background: theme.palette.text.primary,
          fontSize: '0.75rem',
          borderRadius: 8,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderColor: `${theme.palette.divider}`,
        },
      },
    },
    MuiDialog: {
      // The keyboard starts in a dialog's first field (keyboard.js).
      defaultProps: { TransitionProps: { onEntered: focusFirstField } },
      styleOverrides: {
        paper: {
          borderRadius: 20,
          backgroundImage: 'none',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '1.25rem',
          fontWeight: 700,
          lineHeight: 1.35,
          padding: '20px 24px 8px',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: { padding: '8px 24px 16px' },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: { padding: '12px 24px 20px', gap: 8, '& > :not(:first-of-type)': { marginLeft: 0 } },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          boxShadow: theme.shadows[9],
        },
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        root: { backgroundColor: dark ? 'rgba(255,255,255,0.08)' : t.field },
        rounded: { borderRadius: 14 },
      },
    },
  };
}
